import {
  IAccountService,
  IPropertySelection,
  IUserContext,
  ServiceType,
} from "@origin-digital/event-dispatcher";
import { useEffect, useState } from "react";
import { StorageKeys } from "@origin-digital/platform-enums";
import { useFeatureFlags } from "@origin-digital/feature-flags";
import { enrichUserData } from "../UserContext/enrichUserContext";
import {
  copyAndReplaceProperty,
  getInitialPropertyData,
} from "../utils/properties";
import { setAccountNumberQueryParam } from "../utils/queryParams";
import {
  writePropertySelectionToStorage,
  writeServiceSelectionToStorage,
} from "../utils/storage";
import { getAccountServiceIdentifier } from "../utils/mapAccountServices";
import { usePropertySelectionListener } from "./usePropertySelectionListener";

export interface PropertySelectionProps {
  properties: IPropertySelection[] | undefined;
  property: IPropertySelection | null;
  setProperty: (
    value: IPropertySelection | null,
    persist?: boolean,
    applyServiceQueryParam?: boolean
  ) => void;
  setService: (accountService: IAccountService) => void;
}

/**
 * Handle state for properties list, selected property and setting selected property and selected service.
 *
 * Initialise state based on user DB data, URL query params, and local storage.
 *
 * Listen for SUBSCRIBE_SELECTED_PROPERTY subscribers and send updates on selection changes.
 */
export const usePropertySelection = (
  user?: IUserContext
): PropertySelectionProps => {
  const featureFlags = useFeatureFlags();
  const multipleServicesEnabled =
    featureFlags?.internetMultipleServicesPerProperty ?? false;
  const [properties, setProperties] = useState<
    IPropertySelection[] | undefined
  >(undefined);
  const [propertySelection, setPropertySelection] =
    useState<IPropertySelection | null>(null);

  // when user is loaded initialise the properties list and selection
  useEffect(() => {
    const refetchUser = async () => {
      if (user) {
        const enrichedUser = await enrichUserData(user);
        const { propertyList, selectedProperty } = getInitialPropertyData(
          enrichedUser,
          multipleServicesEnabled
        );
        setPropertySelection(selectedProperty ?? null);
        setProperties(propertyList);
      }
    };
    refetchUser();
    // Update property selection if another tab changes it in local storage
    const listener = (data: StorageEvent) => {
      if (data.key === StorageKeys.selectedProperty) {
        refetchUser();
      }
    };
    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [user, multipleServicesEnabled]);

  // listen for subscription events, and notify subscribers when the selection changes
  usePropertySelectionListener(propertySelection, properties);

  /*
   * handler: when a property selection is made, update the state and storage
   */
  const setProperty = (
    value: IPropertySelection | null,
    persist: boolean = true,
    applyServiceQueryParam: boolean = true
  ) => {
    setPropertySelection(value);
    if (persist) {
      writePropertySelectionToStorage(value?.id ?? null);
    }

    // Determine what value to use for accountNumber parameter
    let accountNumberValue: string | undefined;
    if (applyServiceQueryParam && value?.selectedAccountService) {
      const selectedService = value.selectedAccountService;

      // For BROADBAND with feature flag enabled, use serviceId (serviceCode)
      // Otherwise use accountId for backward compatibility
      if (
        multipleServicesEnabled &&
        selectedService.serviceType === ServiceType.BROADBAND &&
        selectedService.serviceId
      ) {
        accountNumberValue = selectedService.serviceId;
      } else {
        accountNumberValue = selectedService.accountId;
      }
    }

    setAccountNumberQueryParam(accountNumberValue);
  };

  /*
   * handler: when a service selection is made, update current property selection, write it to storage, and update properties list
   */
  const setService = (selectedAccountService: IAccountService) => {
    if (!propertySelection) {
      return;
    }
    const newPropertySelection: IPropertySelection = {
      ...propertySelection,
      selectedAccountService,
    };
    const newPropertiesList = copyAndReplaceProperty(
      properties,
      newPropertySelection
    );
    setProperties(newPropertiesList);
    setPropertySelection(newPropertySelection);
    writeServiceSelectionToStorage(
      propertySelection.id,
      getAccountServiceIdentifier(
        selectedAccountService,
        multipleServicesEnabled
      ) ?? ""
    );

    // Determine what value to use for accountNumber parameter
    // For BROADBAND with feature flag enabled, use serviceId (serviceCode)
    // Otherwise use accountId for backward compatibility
    let accountNumberValue: string;
    if (
      multipleServicesEnabled &&
      selectedAccountService.serviceType === ServiceType.BROADBAND &&
      selectedAccountService.serviceId
    ) {
      accountNumberValue = selectedAccountService.serviceId;
    } else {
      accountNumberValue = selectedAccountService.accountId;
    }

    setAccountNumberQueryParam(accountNumberValue);
  };

  return {
    properties,
    property: propertySelection,
    setProperty,
    setService,
  };
};
